import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { General } from 'icons/untitledui';
import html2pdf from 'html2pdf.js';
import { Menu, MenuItem, Alert, Snackbar } from '@mui/material';

// components
import ConversationItemDetails from 'eCoach/convoPrep/ConversationItemDetails';
import ConversationItemPrint from 'eCoach/convoPrep/ConversationItemPrint';

// hooks
import { usePosthogCapture } from 'eCoach/posthogCapture/chat';

// styles
import * as Styled from 'eCoach/_styles/ConvoPrep.style';

interface ConversationItem {
  description?: string;
  prep?: string[];
  reactions?: string[];
  example_language?: string[];
  [key: string]: any;
}

interface ConversationItemClickedProps {
  item: ConversationItem;
  title?: string;
}

export default function ConversationItemClicked({
  item,
  title,
}: ConversationItemClickedProps): JSX.Element {
  const { t } = useTranslation('pageEcoach');
  const chat = usePosthogCapture();
  const contentRef = useRef<HTMLDivElement>(null);
  const printContentRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    chat.messageConvoPrepRetreived();
  }, [chat]);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const handlePrint = async () => {
    handleMenuClose();
    if (!printContentRef.current) return;

    const element = printContentRef.current;
    const opt = {
      margin: 0.5,
      filename: `${title || item?.title}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        logging: true,
        letterRendering: true,
        windowHeight: element.scrollHeight,
      },
      jsPDF: {
        unit: 'in',
        format: 'letter',
        orientation: 'portrait',
      },
    } as const;

    try {
      await html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error('Error generating PDF:', err);
      setError(t('convoPrep.ConversationItemDetails.pdfError'));
    }
  };

  const handleCloseError = () => {
    setError(null);
  };

  if (!item) return <></>;

  return (
    <>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
      <Styled.MenuSection ref={contentRef}>
        <Styled.MenuIcon
          onClick={handleMenuClick}
          aria-haspopup="true"
          role="button"
          aria-label={t('convoPrep.ConversationItemDetails.Open menu')}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleMenuClick(e as unknown as React.MouseEvent<HTMLElement>);
            }
          }}
        >
          <General.DotsVertical size={20} />
        </Styled.MenuIcon>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handlePrint}>
            {t('PrintConversation.Print')}
          </MenuItem>
        </Menu>
        <ConversationItemDetails item={item} />
      </Styled.MenuSection>

      <ConversationItemPrint
        item={item}
        title={title}
        printContentRef={printContentRef}
      />
    </>
  );
}
